import { z } from 'zod';
// The shadow column's vocabulary (B3) — pure, no three. Same import the exploration sub-table takes.
import { ENTITY_SHADOW } from '../../render/lights/lightModel';
import type { RegistryEntry } from '../registry';
// F1: the ONE placement-rotation fragment (yaw + pitch + roll), shared with the main table.
import { PLACEMENT_ROTATION } from '../../world/schema';

/**
 * MAIN-MENU vertical SUB-TABLE (ADR-0001 amendment; main-menu plan §4). An eager-safe table
 * fragment `src/entities/registry.ts` spreads into ENTITY_REGISTRY with ONE line, exactly as the
 * exploration vertical's does. Same contract as the main table: names + zod schemas + meta + lazy
 * `() => import(...)` thunks ONLY. NEVER a static import of an entity class module from this file
 * — `tests/entityBoundary.test.ts` scans every per-vertical table module like `registry.ts`.
 *
 * ⭐ WHY A SUB-TABLE AND NOT A ROW IN `registry.ts` — the decision, stated (ADR-0001 amendment:
 * "New vertical classes go in the vertical's own sub-table, not in registry.ts"). The menu is a
 * VERTICAL, the fourth one, and it needs the same three things the exploration vertical needed:
 *   1. a LOAD TRIGGER derived from a key SET rather than a string literal — `menuRequired()`
 *      (`src/ui/menu/gate.ts`) is `requires.some(name => Object.hasOwn(MENU_ENTITIES, name))`, so a second menu
 *      class arms the trigger with zero edits to the gate, which is the whole point of the shape;
 *   2. a file that is not `registry.ts`, so two parallel tracks never conflict on it again;
 *   3. one place a reader can see the vertical's whole class surface.
 * The cost is one spread line in `registry.ts`, and it is the same line the exploration table
 * already costs. A row in the shared table would have made the gate a literal.
 */

const V3Tuple = z.tuple([z.number(), z.number(), z.number()]);

/**
 * The main-menu STAGE marker. Its PRESENCE in a world's `requires` is what opens the menu gate —
 * the exploration director's rule ("its presence is what makes the world an open-world level; no
 * flag"), applied to the menu. Placement data only; every number the surface draws comes from the
 * data port, and every number it moves by comes from `ui/motion/tokens.ts`.
 */
export const MenuStageSchema = z.object({
  type: z.literal('menu_stage'),
  /** Marker position, world meters (editor ghost only — the stage has no gameplay body). */
  pos: V3Tuple,
  /** Facing, degrees about +Y (0 = game forward, −Z) — the direction the stage is dressed toward. */
  ...PLACEMENT_ROTATION,
});

export type MenuStageDef = z.infer<typeof MenuStageSchema>;

export const MENU_ENTITIES: Record<string, RegistryEntry> = {
  menu_stage: {
    schema: MenuStageSchema,
    meta: {
      // ADR-0002's FIXED five. `logic` is the invisible-machinery tier (`player_start`, triggers,
      // directors) and this marker draws nothing a player ever sees — it declares a capability.
      category: 'logic',
      label: 'Menu stage',
      description:
        'Its presence makes the world a main-menu stage: the menu UI is drawn over it. Editor-only ghost marker; no gameplay.',
      shadow: ENTITY_SHADOW.ghost,
      mobility: 'dynamic',
    },
    load: () => import('./menuStage'),
  },
};
